import DOMPurify from 'dompurify';
import { useEffect, useRef, useState } from 'react';
import styles from './DiffTable.module.scss';
import { Button } from 'components/uikit/buttons/Button.tsx';

type DiffMarker = {
  top: number;
  height: number;
  type: MarkerType;
};

enum MarkerType {
  Added,
  Removed,
  Both,
}
const markerStyles: Record<MarkerType, string> = {
  [MarkerType.Added]: styles.added,
  [MarkerType.Removed]: styles.removed,
  [MarkerType.Both]: styles.both,
};

type DiffTableProps = {
  html: string;
};

export const DiffTable = ({ html }: DiffTableProps) => {
  const HUNDRED_PERCENT = 100;
  const MIN_MARKER_HEIGHT_PERCENT = 0.5;

  const containerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLSpanElement>(null);

  const [diffMarkers, setDiffMarkers] = useState<DiffMarker[]>([]);

  useEffect(() => {
    const container = containerRef.current;
    const table = tableRef.current;

    if (!container || !table) return;

    const calculateMarkers = () => {
      const rows = table.querySelectorAll('tbody tr');

      const scrollHeight = container.scrollHeight;

      const newMarkers: DiffMarker[] = [];

      rows.forEach((row) => {
        const hasAdded = row.querySelector('ins') !== null;
        const hasRemoved = row.querySelector('del') !== null;

        if (!hasAdded && !hasRemoved) return;

        const htmlRow = row as HTMLElement;

        const top = htmlRow.offsetTop;
        const height = htmlRow.offsetHeight;

        let type: MarkerType;

        if (hasAdded && hasRemoved) {
          type = MarkerType.Both;
        } else if (hasAdded) {
          type = MarkerType.Added;
        } else {
          type = MarkerType.Removed;
        }

        newMarkers.push({
          top: (top / scrollHeight) * HUNDRED_PERCENT,
          height: Math.max(
            (height / scrollHeight) * HUNDRED_PERCENT,
            MIN_MARKER_HEIGHT_PERCENT,
          ),
          type,
        });
      });

      setDiffMarkers(newMarkers);
    };

    calculateMarkers();

    const resizeObserver = new ResizeObserver(calculateMarkers);
    resizeObserver.observe(table);

    return () => {
      resizeObserver.disconnect();
    };
  }, [html]);

  const scrollToMarker = (marker: DiffMarker) => {
    const container = containerRef.current;

    if (!container) return;

    container.scrollTo({
      top: (marker.top / HUNDRED_PERCENT) * container.scrollHeight,
      behavior: 'smooth',
    });
  };

  return (
    <div className={styles.wrapper}>
      <div ref={containerRef} className={styles.scrollContainer}>
        <span
          ref={tableRef}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(html),
          }}
          className={styles.table}
        />
      </div>
      <div className={styles.scrollbar}>
        {diffMarkers.map((marker, index) => (
          <Button
            key={`diff-marker-${index}`}
            className={`${styles.marker} ${markerStyles[marker.type]}`}
            style={{
              top: `${marker.top}%`,
              height: `${marker.height}%`,
            }}
            onClick={() => scrollToMarker(marker)}
          />
        ))}
      </div>
    </div>
  );
};
